import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";

import { dayjs } from "@thalamos/common";
import type { ExtendedVisionUser } from "@thalamos/vision-common";
import { UserOrganisationRoleSchema } from "@thalamos/vision-common";

import api, { apiHooks } from "../../../api/index.js";
import { useToastNotifications } from "../../../hooks/useToastNotifications.js";

export function SupervisorSyncExemptionsManagement() {
  const { t } = useTranslation();
  const notifications = useToastNotifications();

  const [{ data: users, loading }, refetch] =
    apiHooks.user.getSupervisorSyncExemptions();

  const exemptUsers: ExtendedVisionUser[] = Array.isArray(users) ? users : [];

  const handleRemoveExemption = async (user: ExtendedVisionUser) => {
    try {
      const response = await api.user.setSupervisorSyncExemption(user.id, {
        exempt: false,
      });
      if (response.status !== 204) {
        notifications.show(
          t("admin.users.supervisorSyncExemptions.removeExemptionError"),
          { severity: "error" },
        );
        return;
      }
      notifications.show(
        t("admin.users.supervisorSyncExemptions.removeExemptionSuccess"),
        { severity: "success" },
      );
      void refetch();
    } catch (_err) {
      notifications.show(
        t("admin.users.supervisorSyncExemptions.removeExemptionError"),
        { severity: "error" },
      );
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        {t("admin.users.supervisorSyncExemptions.title")}
      </Typography>

      <Alert severity="info" sx={{ mb: 3 }}>
        {t("admin.users.supervisorSyncExemptions.bannerBody")}
      </Alert>

      {exemptUsers.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {t("admin.users.supervisorSyncExemptions.noUsers")}
        </Typography>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>
                {t("admin.users.supervisorSyncExemptions.name")}
              </TableCell>
              <TableCell>
                {t("admin.users.supervisorSyncExemptions.email")}
              </TableCell>
              <TableCell>
                {t(
                  "admin.users.supervisorSyncExemptions.supervisorMemberships",
                )}
              </TableCell>
              <TableCell>
                {t("admin.users.supervisorSyncExemptions.dateAdded")}
              </TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {exemptUsers.map((user) => {
              const supervisorMemberships = user.memberships.filter(
                (m) => m.role === UserOrganisationRoleSchema.enum.Supervisor,
              );
              return (
                <TableRow key={user.id}>
                  <TableCell>{user.name}</TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>
                    {supervisorMemberships.length === 0
                      ? "-"
                      : supervisorMemberships
                          .map((m) => m.organisationName)
                          .join(", ")}
                  </TableCell>
                  <TableCell>
                    {user.supervisorSyncExemptionDate
                      ? dayjs(user.supervisorSyncExemptionDate).format(
                          "DD/MM/YYYY HH:mm",
                        )
                      : "-"}
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="outlined"
                      color="error"
                      size="small"
                      onClick={() => {
                        void handleRemoveExemption(user);
                      }}
                    >
                      {t(
                        "admin.users.supervisorSyncExemptions.removeExemption",
                      )}
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
    </Box>
  );
}
